<template>
  <div class="page-container">
    <div class="knowledge-layout">
      <!-- 左侧：分类管理 -->
      <div class="category-panel">
        <div class="panel-header">
          <h3>知识分类</h3>
          <el-button type="primary" size="small" @click="openCategoryDialog(null)">
            <el-icon><Plus /></el-icon>
            新增
          </el-button>
        </div>
        <div class="category-list">
          <!-- 树形分类结构（扁平化渲染，避免递归组件问题） -->
          <div
            v-for="item in categoryTreeFlat"
            :key="item.id"
            class="category-item"
            :class="{ active: currentCategoryId === item.id }"
            :style="{ paddingLeft: (16 + item.depth * 20) + 'px' }"
            @click="selectCategory(item)"
            @mouseenter="hoverCatId = item.id"
            @mouseleave="hoverCatId = 0"
          >
            <div class="category-info">
              <span
                class="expand-icon"
                :class="{ invisible: !item.hasChildren }"
                @click.stop="toggleExpand(item.id)"
              >
                <el-icon :style="{ transform: !collapsedIds.includes(item.id) ? 'rotate(90deg)' : 'rotate(0deg)', transition: '0.2s' }">
                  <ArrowRight />
                </el-icon>
              </span>
              <el-icon :style="{ color: item.hasChildren ? '#e6a23c' : '#c0c4cc' }">
                <Folder />
              </el-icon>
              <span class="category-name">{{ item.category_name }}</span>
              <el-tag size="small" type="info" class="id-tag">ID:{{ item.id }}</el-tag>
              <el-tag size="small" type="info" class="count-tag">{{ item.knowledge_count }}</el-tag>
            </div>
            <div class="category-actions" v-show="currentCategoryId === item.id || hoverCatId === item.id">
              <el-button link size="small" @click.stop="openCategoryDialog(item)">
                <el-icon><Edit /></el-icon>
              </el-button>
              <el-button link size="small" type="danger" @click.stop="deleteCategory(item)">
                <el-icon><Delete /></el-icon>
              </el-button>
            </div>
          </div>
          <el-empty v-if="categories.length === 0" description="暂无分类" :image-size="60" />
        </div>
        <div class="panel-footer">
          <el-button link type="primary" @click="currentCategoryId = null; fetchKnowledgeList()">
            查看全部知识
          </el-button>
        </div>
      </div>

      <!-- 右侧：知识内容管理 -->
      <div class="content-panel">
        <!-- 搜索和操作栏 -->
        <div class="toolbar">
          <div class="search-area">
            <el-input
              v-model="searchKeyword"
              placeholder="搜索知识标题或内容..."
              size="large"
              style="width: 350px"
              clearable
              @keyup.enter="handleSearch"
              @clear="handleSearch"
            >
              <template #prefix>
                <el-icon><Search /></el-icon>
              </template>
            </el-input>
            <el-button type="primary" size="large" @click="handleSearch">
              <el-icon><Search /></el-icon>
              搜索
            </el-button>
          </div>
          <el-button type="success" size="large" @click="openKnowledgeDialog(null)">
            <el-icon><Plus /></el-icon>
            新增知识
          </el-button>
          <el-button type="warning" size="large" @click="openBatchImportDialog">
            <el-icon><Upload /></el-icon>
            批量导入
          </el-button>
        </div>

        <!-- 知识列表 -->
        <el-table
          :data="knowledgeList"
          stripe
          style="width: 100%; margin-top: 16px"
          v-loading="loading"
        >
          <el-table-column prop="title" label="知识标题" min-width="200" show-overflow-tooltip />
          <el-table-column label="所属分类" width="140">
            <template #default="{ row }">
              {{ row.category_name || '未分类' }}
            </template>
          </el-table-column>
          <el-table-column label="内容预览" min-width="250" show-overflow-tooltip>
            <template #default="{ row }">
              {{ stripHtml(row.content).substring(0, 80) }}
            </template>
          </el-table-column>
          <el-table-column label="状态" width="80">
            <template #default="{ row }">
              <el-tag :type="row.status === 1 ? 'success' : 'info'" size="small">
                {{ row.status === 1 ? '正常' : '禁用' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="use_num" label="引用" width="60" align="center" />
          <el-table-column prop="create_time" label="创建时间" width="160" />
          <el-table-column label="操作" width="180" fixed="right" class-name="operation-column">
            <template #default="{ row }">
              <div class="operation-btns">
                <el-button type="primary" size="small" link @click="openKnowledgeDialog(row)">
                  <el-icon><Edit /></el-icon>
                  编辑
                </el-button>
                <el-button type="primary" size="small" link @click="previewKnowledge(row)">
                  <el-icon><View /></el-icon>
                  预览
                </el-button>
                <el-button type="danger" size="small" link @click="deleteKnowledge(row)">
                  <el-icon><Delete /></el-icon>
                  删除
                </el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>

        <div class="pagination-container">
          <el-pagination
            v-model:current-page="pagination.page"
            v-model:page-size="pagination.pageSize"
            :total="pagination.total"
            :page-sizes="[10, 20, 30, 50]"
            layout="total, sizes, prev, pager, next, jumper"
            @size-change="fetchKnowledgeList"
            @current-change="fetchKnowledgeList"
          />
        </div>
      </div>
    </div>

    <!-- 分类新增/编辑对话框 -->
    <el-dialog
      v-model="categoryDialogVisible"
      :title="editingCategory ? '编辑分类' : '新增分类'"
      width="500px"
      :close-on-click-modal="false"
    >
      <el-form ref="categoryForm" :model="categoryForm" :rules="categoryRules" label-width="100px">
        <el-form-item label="分类名称" prop="category_name">
          <el-input v-model="categoryForm.category_name" placeholder="请输入分类名称" size="large" />
        </el-form-item>
        <el-form-item label="父级分类">
          <el-select v-model="categoryForm.parent_id" placeholder="选择父级分类（可选）" style="width: 100%" clearable>
            <el-option label="顶级分类" :value="0" />
            <el-option
              v-for="cat in categories"
              :key="cat.id"
              :label="cat.category_name"
              :value="cat.id"
              :disabled="cat.id === editingCategory?.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="排序">
          <el-input-number v-model="categoryForm.sort" :min="0" :max="999" controls-position="right" style="width: 100%" />
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="categoryForm.notes" placeholder="备注信息（可选）" type="textarea" :rows="2" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="categoryDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitCategoryForm">确认</el-button>
      </template>
    </el-dialog>

    <!-- 知识新增/编辑对话框 -->
    <el-dialog
      v-model="knowledgeDialogVisible"
      :title="editingKnowledge ? '编辑知识' : '新增知识'"
      width="900px"
      :close-on-click-modal="false"
      top="5vh"
    >
      <el-form ref="knowledgeForm" :model="knowledgeForm" :rules="knowledgeRules" label-width="100px">
        <el-row :gutter="20">
          <el-col :span="16">
            <el-form-item label="知识标题" prop="title">
              <el-input v-model="knowledgeForm.title" placeholder="请输入知识标题" size="large" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="所属分类">
              <el-select v-model="knowledgeForm.category_id" placeholder="选择分类" style="width: 100%" clearable>
                <el-option label="未分类" :value="0" />
                <el-option v-for="cat in categories" :key="cat.id" :label="cat.category_name" :value="cat.id" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="知识内容" prop="content">
          <el-input
            v-model="knowledgeForm.content"
            type="textarea"
            placeholder="请输入知识内容（支持换行）"
            :rows="14"
            resize="vertical"
            style="font-family: monospace; font-size: 14px; line-height: 1.8;"
          />
        </el-form-item>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="来源URL">
              <el-input v-model="knowledgeForm.source_url" placeholder="可选，输入文章来源链接" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="状态">
              <el-radio-group v-model="knowledgeForm.status">
                <el-radio :label="1">正常</el-radio>
                <el-radio :label="2">禁用</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="备注">
          <el-input v-model="knowledgeForm.notes" placeholder="备注信息（可选）" type="textarea" :rows="2" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="knowledgeDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitKnowledgeForm">确认</el-button>
      </template>
    </el-dialog>

    <!-- 知识预览对话框 -->
    <el-dialog
      v-model="previewVisible"
      title="知识预览"
      width="800px"
      :close-on-click-modal="false"
      top="5vh"
    >
      <div class="preview-content" v-if="previewData">
        <div class="preview-meta">
          <span class="meta-item">
            <el-tag size="small">{{ previewData.category_name || '未分类' }}</el-tag>
          </span>
          <span class="meta-item" v-if="previewData.source_url">
            <el-icon><Link /></el-icon>
            <a :href="previewData.source_url" target="_blank">来源链接</a>
          </span>
          <span class="meta-item">
            创建时间: {{ previewData.create_time }}
          </span>
        </div>
        <h2 class="preview-title">{{ previewData.title }}</h2>
        <div class="preview-body" v-html="formatContent(previewData.content)"></div>
      </div>
      <template #footer>
        <el-button @click="previewVisible = false">关闭</el-button>
      </template>
    </el-dialog>

    <!-- 批量导入对话框 -->
    <el-dialog
      v-model="batchImportVisible"
      title="批量导入知识"
      width="700px"
      :close-on-click-modal="false"
    >
      <el-form :model="batchImportForm" label-width="100px">
        <el-form-item label="目标分类">
          <el-select v-model="batchImportForm.category_id" placeholder="选择分类（可选）" style="width: 100%" clearable>
            <el-option label="未分类" :value="0" />
            <el-option v-for="cat in categories" :key="cat.id" :label="cat.category_name" :value="cat.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="URL列表">
          <el-input
            v-model="batchImportForm.urls"
            type="textarea"
            :rows="10"
            placeholder="请输入要导入的URL地址，每行一个&#10;例如：&#10;https://example.com/article1&#10;https://example.com/article2&#10;example.com/article3"
          />
          <div class="import-hint">
            支持换行、逗号或空格分隔。系统会自动抓取网页标题作为知识标题，正文作为知识内容。
          </div>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="batchImportVisible = false">取消</el-button>
        <el-button type="primary" @click="submitBatchImport" :loading="batchImporting">
          {{ batchImporting ? '正在导入...' : '开始导入' }}
        </el-button>
      </template>
    </el-dialog>

    <!-- 导入结果对话框 -->
    <el-dialog
      v-model="importResultVisible"
      title="导入结果"
      width="600px"
      :close-on-click-modal="false"
    >
      <div class="import-result">
        <el-result
          :icon="importResult.errors && importResult.errors.length > 0 ? 'warning' : 'success'"
          :title="`成功导入 ${importResult.imported || 0} / ${importResult.total || 0} 条`"
        >
          <template #extra v-if="importResult.errors && importResult.errors.length > 0">
            <div class="import-errors">
              <h4>失败详情：</h4>
              <ul>
                <li v-for="(err, idx) in importResult.errors" :key="idx">{{ err }}</li>
              </ul>
            </div>
          </template>
        </el-result>
      </div>
      <template #footer>
        <el-button type="primary" @click="importResultVisible = false">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import Cookies from 'js-cookie';
import {
  Search, Plus, Edit, Delete, View, Folder, Link, Upload, ArrowRight
} from '@element-plus/icons-vue';

export default {
  name: 'KnowledgeBase',
  components: { Search, Plus, Edit, Delete, View, Folder, Link, Upload, ArrowRight },
  data() {
    return {
      loading: false,

      // 分类
      categories: [],
      currentCategoryId: null,
      hoverCatId: 0,
      collapsedIds: [], // 记录折叠的分类ID
      categoryDialogVisible: false,
      editingCategory: null,
      categoryForm: {
        category_name: '',
        parent_id: 0,
        sort: 0,
        notes: ''
      },
      categoryRules: {
        category_name: [
          { required: true, message: '请输入分类名称', trigger: 'blur' },
          { min: 1, max: 100, message: '长度在 1 到 100 个字符', trigger: 'blur' }
        ]
      },

      // 知识列表
      knowledgeList: [],
      searchKeyword: '',
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },

      // 知识编辑
      knowledgeDialogVisible: false,
      editingKnowledge: null,
      knowledgeForm: {
        title: '',
        category_id: 0,
        content: '',
        source_url: '',
        status: 1,
        notes: ''
      },
      knowledgeRules: {
        title: [
          { required: true, message: '请输入知识标题', trigger: 'blur' },
          { min: 1, max: 255, message: '长度在 1 到 255 个字符', trigger: 'blur' }
        ]
      },

      // 预览
      previewVisible: false,
      previewData: null,

      // 批量导入
      batchImportVisible: false,
      batchImporting: false,
      batchImportForm: {
        category_id: 0,
        urls: ''
      },
      importResultVisible: false,
      importResult: {},
    };
  },
  computed: {
    userInfo() {
      return JSON.parse(Cookies.get('user_info') || '{}');
    },
    apiBase() {
      return `${config.BASE_URL}/shop/api/KnowledgeBaseController.php`;
    },
    // 构建扁平化分类列表（支持展开/折叠和层级缩进）
    categoryTreeFlat() {
      const list = this.categories || [];
      if (list.length === 0) return [];

      // 建立id索引
      const map = {};
      list.forEach(item => {
        map[item.id] = {
          ...item,
          children: [],
          hasChildren: false
        };
      });

      // 构建父子关系
      const roots = [];
      list.forEach(item => {
        const node = map[item.id];
        if (item.parent_id && map[item.parent_id]) {
          map[item.parent_id].children.push(node);
          map[item.parent_id].hasChildren = true;
        } else {
          roots.push(node);
        }
      });

      // 扁平化遍历（深度优先，跳过折叠节点的子节点）
      const result = [];
      const walk = (nodes, depth) => {
        nodes.forEach(node => {
          node.depth = depth;
          result.push(node);
          const isExpanded = !this.collapsedIds.includes(node.id);
          if (isExpanded && node.children.length) {
            walk(node.children, depth + 1);
          }
        });
      };
      walk(roots, 0);
      return result;
    }
  },
  mounted() {
    this.fetchCategories();
    this.fetchKnowledgeList();
  },
  methods: {
    // 去除HTML标签
    stripHtml(html) {
      if (!html) return '';
      return html.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ');
    },

    // 格式化内容预览
    formatContent(content) {
      if (!content) return '<p style="color: #999;">暂无内容</p>';
      // 将换行转为<p>标签，保持换行
      return content
        .split('\n')
        .map(line => line.trim() ? `<p style="margin: 8px 0; line-height: 1.8;">${line}</p>` : '<br/>')
        .join('');
    },

    // ==================== 分类相关 ====================

    async fetchCategories() {
      try {
        const { data } = await axios.post(this.apiBase, {
          action: 'list_categories',
          store_id: this.userInfo.id
        });
        if (data.data) {
          this.categories = data.data;
        }
      } catch (error) {
        console.error('获取分类列表错误:', error);
      }
    },

    selectCategory(cat) {
      this.currentCategoryId = cat.id;
      this.pagination.page = 1;
      this.searchKeyword = '';
      this.fetchKnowledgeList();
    },

    toggleExpand(id) {
      const idx = this.collapsedIds.indexOf(id);
      if (idx > -1) {
        this.collapsedIds.splice(idx, 1);
      } else {
        this.collapsedIds.push(id);
      }
    },

    openCategoryDialog(cat) {
      this.editingCategory = cat;
      if (cat) {
        this.categoryForm = {
          category_name: cat.category_name,
          parent_id: cat.parent_id || 0,
          sort: cat.sort || 0,
          notes: cat.notes || ''
        };
      } else {
        this.categoryForm = {
          category_name: '',
          parent_id: 0,
          sort: 0,
          notes: ''
        };
      }
      this.categoryDialogVisible = true;
    },

    submitCategoryForm() {
      this.$refs.categoryForm.validate(async (valid) => {
        if (!valid) return;
        try {
          const action = this.editingCategory ? 'update_category' : 'add_category';
          const payload = {
            action,
            store_id: this.userInfo.id,
            ...this.categoryForm
          };
          if (this.editingCategory) {
            payload.id = this.editingCategory.id;
          }

          const { data } = await axios.post(this.apiBase, payload);
          if (data.success) {
            this.$message.success(this.editingCategory ? '分类更新成功' : '分类新增成功');
            this.categoryDialogVisible = false;
            this.fetchCategories();
            this.fetchKnowledgeList();
          } else {
            this.$message.error(data.error || '操作失败');
          }
        } catch (error) {
          const errMsg = error.response?.data?.error || error.message || '操作失败';
          this.$message.error(`操作失败: ${errMsg}`);
        }
      });
    },

    async deleteCategory(cat) {
      this.$confirm(`确定删除分类「${cat.category_name}」吗？该分类下的所有知识也会被删除！`, '警告', {
        confirmButtonText: '确认删除',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const { data } = await axios.post(this.apiBase, {
            action: 'delete_category',
            store_id: this.userInfo.id,
            id: cat.id
          });
          if (data.success) {
            this.$message.success('分类已删除');
            if (this.currentCategoryId === cat.id) {
              this.currentCategoryId = null;
            }
            this.fetchCategories();
            this.fetchKnowledgeList();
          } else {
            this.$message.error(data.error || '删除失败');
          }
        } catch (error) {
          this.$message.error('删除失败: ' + (error.response?.data?.error || error.message));
        }
      }).catch(() => {});
    },

    // ==================== 知识内容相关 ====================

    async fetchKnowledgeList() {
      this.loading = true;
      try {
        const isSearch = !!this.searchKeyword.trim();
        const action = isSearch ? 'search' : 'list';
        const payload = {
          action,
          store_id: this.userInfo.id,
          page: this.pagination.page,
          page_size: this.pagination.pageSize
        };

        if (isSearch) {
          payload.keyword = this.searchKeyword.trim();
        } else if (this.currentCategoryId) {
          payload.category_id = this.currentCategoryId;
        }

        const { data } = await axios.post(this.apiBase, payload);
        if (data.data) {
          this.knowledgeList = data.data;
          this.pagination.total = data.pagination?.total || data.data.length;
        } else {
          this.knowledgeList = [];
          this.pagination.total = 0;
        }
      } catch (error) {
        console.error('获取知识列表错误:', error);
        this.$message.error('获取知识列表失败');
      } finally {
        this.loading = false;
      }
    },

    handleSearch() {
      this.pagination.page = 1;
      this.currentCategoryId = null;
      this.fetchKnowledgeList();
    },

    openKnowledgeDialog(knowledge) {
      this.editingKnowledge = knowledge;
      if (knowledge) {
        this.knowledgeForm = {
          title: knowledge.title,
          category_id: knowledge.category_id || 0,
          content: knowledge.content || '',
          source_url: knowledge.source_url || '',
          status: knowledge.status || 1,
          notes: knowledge.notes || ''
        };
      } else {
        this.knowledgeForm = {
          title: '',
          category_id: this.currentCategoryId || 0,
          content: '',
          source_url: '',
          status: 1,
          notes: ''
        };
      }
      this.knowledgeDialogVisible = true;
    },

    submitKnowledgeForm() {
      this.$refs.knowledgeForm.validate(async (valid) => {
        if (!valid) return;
        try {
          const action = this.editingKnowledge ? 'update' : 'add';
          const payload = {
            action,
            store_id: this.userInfo.id,
            ...this.knowledgeForm
          };
          if (this.editingKnowledge) {
            payload.id = this.editingKnowledge.id;
          }

          const { data } = await axios.post(this.apiBase, payload);
          if (data.success) {
            this.$message.success(this.editingKnowledge ? '知识更新成功' : '知识新增成功');
            this.knowledgeDialogVisible = false;
            this.fetchKnowledgeList();
            this.fetchCategories(); // 刷新分类计数
          } else {
            this.$message.error(data.error || '操作失败');
          }
        } catch (error) {
          const errMsg = error.response?.data?.error || error.message || '操作失败';
          this.$message.error(`操作失败: ${errMsg}`);
        }
      });
    },

    previewKnowledge(knowledge) {
      this.previewData = knowledge;
      this.previewVisible = true;
    },

    async deleteKnowledge(knowledge) {
      this.$confirm(`确定删除知识「${knowledge.title}」吗？`, '警告', {
        confirmButtonText: '确认删除',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const { data } = await axios.post(this.apiBase, {
            action: 'delete',
            store_id: this.userInfo.id,
            id: knowledge.id
          });
          if (data.success) {
            this.$message.success('知识已删除');
            this.fetchKnowledgeList();
            this.fetchCategories();
          } else {
            this.$message.error(data.error || '删除失败');
          }
        } catch (error) {
          this.$message.error('删除失败: ' + (error.response?.data?.error || error.message));
        }
      }).catch(() => {});
    },

    // ==================== 批量导入相关 ====================

    openBatchImportDialog() {
      this.batchImportForm = {
        category_id: this.currentCategoryId || 0,
        urls: ''
      };
      this.batchImportVisible = true;
    },

    async submitBatchImport() {
      if (!this.batchImportForm.urls.trim()) {
        this.$message.warning('请输入要导入的URL地址');
        return;
      }

      this.batchImporting = true;
      try {
        const { data } = await axios.post(this.apiBase, {
          action: 'batch_import',
          store_id: this.userInfo.id,
          category_id: this.batchImportForm.category_id,
          urls: this.batchImportForm.urls
        });

        this.batchImportVisible = false;

        if (data.data) {
          this.importResult = data.data;
          this.importResultVisible = true;
          // 刷新列表
          this.fetchKnowledgeList();
          this.fetchCategories();
        } else if (data.error) {
          this.$message.error(data.error);
        }
      } catch (error) {
        this.$message.error('批量导入失败: ' + (error.response?.data?.error || error.message));
      } finally {
        this.batchImporting = false;
      }
    }
  }
};
</script>

<style scoped lang="scss">
@use '@/assets/styles/page-common.scss' as *;

.page-container {
  height: calc(100vh - 80px);
}

.knowledge-layout {
  display: flex;
  gap: 20px;
  height: 100%;
  max-width: 100%;
}

/* 左侧分类面板 */
.category-panel {
  width: 260px;
  min-width: 260px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;

  .panel-header {
    padding: 16px 20px;
    border-bottom: 1px solid #ebeef5;
    display: flex;
    justify-content: space-between;
    align-items: center;

    h3 {
      margin: 0;
      font-size: 16px;
      color: #1f2937;
      font-weight: 600;
    }
  }

  .category-list {
    flex: 1;
    overflow-y: auto;
    padding: 8px 0;
  }

  .category-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 20px;
    cursor: pointer;
    transition: all 0.2s;
    border-left: 3px solid transparent;

    &:hover {
      background: #f0f7ff;
    }

    &.active {
      background: #ecf5ff;
      border-left-color: #409EFF;
      color: #409EFF;
    }

    .category-info {
      display: flex;
      align-items: center;
      gap: 6px;
      flex: 1;
      min-width: 0;

      .expand-icon {
        cursor: pointer;
        display: flex;
        align-items: center;
        flex-shrink: 0;

        &.invisible {
          visibility: hidden;
        }

        .el-icon {
          font-size: 12px;
          color: #909399;
        }
      }

      .el-icon {
        font-size: 14px;
        flex-shrink: 0;
      }

      .category-name {
        font-size: 14px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        flex: 1;
      }

      .id-tag {
        flex-shrink: 0;
        font-size: 11px;
        padding: 0 4px;
        height: 18px;
      }

      .count-tag {
        flex-shrink: 0;
      }
    }

    .category-actions {
      display: flex;
      gap: 4px;
      flex-shrink: 0;
      margin-left: 8px;
    }
  }

  .panel-footer {
    padding: 12px 20px;
    border-top: 1px solid #ebeef5;
    text-align: center;
  }
}

/* 右侧内容面板 */
.content-panel {
  flex: 1;
  min-width: 0;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  padding: 24px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;

  .search-area {
    display: flex;
    align-items: center;
    gap: 12px;
  }
}

.pagination-container {
  flex-shrink: 0;
}

/* 操作按钮并排 */
.operation-btns {
  display: flex;
  align-items: center;
  gap: 0;
  white-space: nowrap;

  .el-button {
    padding: 5px 4px;
    font-size: 12px;
  }
}

/* 预览样式 */
.preview-content {
  .preview-meta {
    display: flex;
    gap: 16px;
    margin-bottom: 20px;
    padding-bottom: 16px;
    border-bottom: 1px solid #ebeef5;
    font-size: 13px;
    color: #909399;

    .meta-item {
      display: flex;
      align-items: center;
      gap: 4px;

      a {
        color: #409EFF;
        text-decoration: none;

        &:hover {
          text-decoration: underline;
        }
      }
    }
  }

  .preview-title {
    font-size: 22px;
    color: #1f2937;
    margin-bottom: 20px;
    line-height: 1.4;
  }

  .preview-body {
    font-size: 15px;
    color: #374151;
    line-height: 1.8;
    max-height: 450px;
    overflow-y: auto;
  }
}

/* 表格优化 */
.el-table {
  flex: 1;
  overflow: auto;

  :deep(.el-table__header) th {
    background-color: #f5f7fa;
    color: #606266;
    font-weight: 600;
  }

  :deep(.el-table__body) tr:hover > td {
    background-color: #f0f7ff !important;
  }
}

/* 对话框优化 */
::deep(.el-dialog) {
  border-radius: 12px;

  .el-dialog__header {
    padding: 20px 24px;
    border-bottom: 1px solid #ebeef5;
    margin-right: 0;

    .el-dialog__title {
      font-weight: 600;
      color: #1f2937;
      font-size: 18px;
    }
  }

  .el-dialog__body {
    padding: 24px;
  }

  .el-dialog__footer {
    padding: 16px 24px;
    border-top: 1px solid #ebeef5;
  }
}

/* 批量导入提示 */
.import-hint {
  margin-top: 8px;
  font-size: 12px;
  color: #909399;
  line-height: 1.6;
}

/* 导入结果 */
.import-result {
  .import-errors {
    text-align: left;
    margin-top: 16px;

    h4 {
      margin: 0 0 8px 0;
      color: #e6a23c;
    }

    ul {
      margin: 0;
      padding-left: 20px;

      li {
        font-size: 13px;
        color: #909399;
        line-height: 1.8;
        word-break: break-all;
      }
    }
  }
}
</style>
